import React from "react";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import {
  ArrowRight,
  BadgeCheck,
  Check,
  Minus,
  Sparkles,
  Star,
  FileText,
  Award,
  Layers,
  Phone,
} from "lucide-react";
import {
  pricingPackages,
  pricingHelperLine,
  featureComparison,
  packageChooser,
  multiModuleDiscounts,
  multiModuleNote,
  pricingFaqs,
} from "../mock";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const iconMap = { FileText, Award, Layers };

function FeatureCell({ value }) {
  if (value === "Yes") {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-teal-soft text-teal">
        <Check size={15} strokeWidth={2.6} />
      </span>
    );
  }
  if (value === "No" || value === "—" || value === "N/A") {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-400">
        <Minus size={14} />
      </span>
    );
  }
  return <span className="text-[13px] font-semibold text-navy">{value}</span>;
}

function PackageCard({ pkg }) {
  const Icon = iconMap[pkg.icon] || FileText;
  const popular = pkg.popular;
  const customised = pkg.customQuote;

  return (
    <div
      data-testid={`pricing-card-${pkg.slug}`}
      className={`relative bg-white rounded-3xl border ${
        popular
          ? "border-teal shadow-2xl lg:-translate-y-3"
          : customised
          ? "border-navy/25 shadow-md"
          : "border-slate-200 shadow-md"
      } card-soft flex flex-col overflow-hidden`}
    >
      {pkg.badge && (
        <div
          className={`absolute top-0 left-0 right-0 text-white text-center text-[11px] font-bold uppercase tracking-[0.18em] py-2 flex items-center justify-center gap-1.5 ${
            popular ? "bg-teal" : "bg-navy"
          }`}
          data-testid={`pricing-badge-${pkg.slug}`}
        >
          {popular ? <Sparkles size={13} /> : <Star size={13} />} {pkg.badge}
        </div>
      )}

      <div className={`p-8 md:p-9 flex flex-col flex-1 ${pkg.badge ? "pt-12" : ""}`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal">
          Package {pkg.number}
        </span>

        <div className="mt-4 flex items-start gap-4">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
              popular ? "bg-teal text-white" : customised ? "bg-navy text-white" : "bg-teal-soft text-teal"
            }`}
          >
            <Icon size={26} />
          </div>
          <div className="flex-1">
            <h3
              className="text-navy font-bold text-[22px] leading-tight"
              data-testid={`pricing-name-${pkg.slug}`}
            >
              {pkg.name}
            </h3>
            <div
              className="mt-2 text-2xl font-bold text-navy"
              data-testid={`pricing-startprice-${pkg.slug}`}
            >
              {pkg.startingPrice}
            </div>
            {pkg.priceSub && (
              <div className="mt-1 text-sm font-medium text-teal">{pkg.priceSub}</div>
            )}
          </div>
        </div>

        <p className="mt-5 text-[15px] text-slate-700 leading-relaxed italic">
          {pkg.description}
        </p>

        <div className="mt-5 p-4 rounded-xl bg-mist border border-slate-200">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal mb-2">
            Best for
          </p>
          <p className="text-sm text-slate-700 leading-relaxed">{pkg.suitableFor}</p>
        </div>

        {/* Pricing breakdown */}
        {pkg.breakdown && pkg.breakdown.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal mb-2.5">
              Pricing
            </p>
            <div className="space-y-1.5">
              {pkg.breakdown.map((b) => (
                <div
                  key={b.type}
                  className="flex items-baseline justify-between gap-3 text-[13.5px] border-b border-dashed border-slate-200 pb-1.5 last:border-b-0"
                >
                  <span className="text-slate-600">{b.type}</span>
                  <span className="font-semibold text-navy whitespace-nowrap">{b.price}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What's included */}
        <div className="mt-6 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal mb-2.5">
            What's included
          </p>
          <ul className="space-y-2.5">
            {pkg.includes.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2.5 text-[14.5px] text-slate-700 leading-snug"
              >
                <Check size={16} className="text-teal mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Tailored pricing note (Stage 2) */}
        {pkg.tailoredPricing && (
          <div className="mt-5 p-4 rounded-xl bg-navy/[0.04] border border-navy/10">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-navy mb-1.5">
              Tailored pricing
            </p>
            <p className="text-[13.5px] text-slate-600 leading-relaxed">{pkg.tailoredPricing}</p>
          </div>
        )}

        <Link
          to="/contact"
          data-testid={`pricing-cta-${pkg.slug}`}
          className={`mt-7 inline-flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-xl font-semibold text-sm transition ${
            popular ? "btn-primary" : "btn-secondary"
          }`}
        >
          {pkg.cta} <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}

export default function Pricing() {
  return (
    <main>
      <PageHero
        eyebrow="Pricing"
        title="Our Packages"
        subtitle="Practical NDIS registration, documentation and audit-readiness support designed for providers at different stages of their compliance journey."
        breadcrumb={[{ label: "Pricing" }]}
      />

      {/* Three pricing cards */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
            {pricingPackages.map((pkg) => (
              <PackageCard key={pkg.slug} pkg={pkg} />
            ))}
          </div>

          <p
            className="mt-10 text-center text-sm text-slate-500 max-w-3xl mx-auto leading-relaxed"
            data-testid="pricing-helper-line"
          >
            {pricingHelperLine}
          </p>
        </div>
      </section>

      {/* Unified feature comparison table */}
      <section className="py-16 md:py-20 bg-mist">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="Compare Packages"
            title="Compare Your Options"
            subtitle="See the key differences between our three levels of support."
          />

          <div
            className="mt-12 bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden"
            data-testid="feature-comparison-table"
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="bg-navy text-white">
                    {featureComparison.headers.map((h, i) => (
                      <th
                        key={i}
                        className={`px-5 py-4 font-bold text-[12.5px] uppercase tracking-wider align-top ${
                          i === 0 ? "text-left" : "text-center"
                        } ${i === 2 ? "bg-teal" : ""}`}
                      >
                        {h}
                        {featureComparison.subHeaders[i] && (
                          <span className="block text-[10px] font-semibold tracking-[0.18em] mt-1 text-white/90">
                            {featureComparison.subHeaders[i]}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {featureComparison.rows.map((row, idx) => (
                    <tr
                      key={row.label}
                      className={`border-b border-slate-100 last:border-b-0 ${
                        idx % 2 === 1 ? "bg-slate-50/60" : "bg-white"
                      }`}
                    >
                      <td className="px-5 py-3.5 text-navy font-semibold text-[14px]">
                        {row.label}
                      </td>
                      {row.values.map((v, j) => (
                        <td
                          key={j}
                          className={`px-5 py-3.5 text-center ${
                            j === 1 ? "bg-teal-soft/30" : ""
                          }`}
                        >
                          <FeatureCell value={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Which package is right for you? */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <SectionHeading
            align="center"
            eyebrow="Quick Guide"
            title="Which Package Is Right for You?"
          />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="package-chooser">
            {packageChooser.map((c) => (
              <div
                key={c.slug}
                className="group bg-white rounded-2xl p-7 border border-slate-200 card-soft hover:border-teal transition flex flex-col"
                data-testid={`chooser-${c.slug}`}
              >
                <h4 className="text-navy font-bold text-lg">{c.title}</h4>
                <p className="mt-3 text-[15px] text-slate-600 leading-relaxed flex-1">{c.text}</p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal group-hover:gap-2.5 transition-all"
                  data-testid={`chooser-cta-${c.slug}`}
                >
                  {c.cta} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-module discount table */}
      <section className="pb-16">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Multi-Module Discount"
                title="Combine Modules and Save"
                subtitle="Providers requiring multiple supplementary modules can take advantage of our multi-module discounts when purchasing eligible documentation or registration packages."
              />
            </div>
            <div className="lg:col-span-7">
              <div
                className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden"
                data-testid="multi-module-discount-table"
              >
                <table className="w-full">
                  <thead>
                    <tr className="bg-navy text-white">
                      <th className="text-left px-6 py-4 text-[12px] uppercase tracking-wider font-bold">
                        Modules required
                      </th>
                      <th className="text-right px-6 py-4 text-[12px] uppercase tracking-wider font-bold">
                        Discount applied
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {multiModuleDiscounts.map((d, i) => (
                      <tr
                        key={d.label}
                        className={`border-b border-slate-100 last:border-b-0 ${
                          i % 2 === 1 ? "bg-slate-50/60" : "bg-white"
                        }`}
                      >
                        <td className="px-6 py-4 text-navy font-semibold">{d.label}</td>
                        <td className="px-6 py-4 text-right text-teal font-bold">{d.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p
                className="mt-4 text-[13px] text-slate-500 leading-relaxed"
                data-testid="multi-module-note"
              >
                {multiModuleNote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Quality Commitment */}
      <section className="pb-12">
        <div className="container-x">
          <div className="bg-navy text-white rounded-3xl p-8 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-teal/15 to-transparent" />
            <div className="lg:col-span-2 relative flex lg:justify-center">
              <BadgeCheck size={64} className="text-teal-mid" />
            </div>
            <div className="lg:col-span-7 relative">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-light">
                Documentation Quality Commitment
              </p>
              <h3 className="mt-2 text-xl md:text-2xl font-bold">
                We stand behind the quality of our documentation.
              </h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                If a compliance issue arises directly because of documentation supplied by Apex, we
                will work with you to correct and resolve the relevant documentation issue at no
                additional cost.
              </p>
            </div>
            <div className="lg:col-span-3 relative flex lg:justify-end">
              <Link
                to="/quality-commitment"
                className="btn-primary px-5 py-3 rounded-lg font-semibold text-sm inline-flex items-center gap-2"
                data-testid="quality-commitment-link"
              >
                Read More <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-mist">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="FAQ"
              title="Frequently Asked Questions"
              subtitle="Clear answers to common questions about our pricing, registration support and audit-readiness packages."
            />
            <div className="mt-8 p-5 bg-white rounded-xl border border-slate-200 card-soft">
              <div className="flex items-center gap-3">
                <Phone size={20} className="text-teal" />
                <p className="text-sm font-semibold text-navy">Need a Custom Quote?</p>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Speak with the Apex team about your registration, supplementary modules, audit
                preparation or customised Stage 2 requirements.
              </p>
              <Link
                to="/contact"
                className="mt-4 btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
                data-testid="pricing-faq-book-cta"
              >
                Book Discovery Call <ArrowRight size={15} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <Accordion
              type="single"
              collapsible
              className="space-y-3"
              data-testid="pricing-faq-accordion"
            >
              {pricingFaqs.map((f, idx) => (
                <AccordionItem
                  key={idx}
                  value={`item-${idx}`}
                  className="bg-white rounded-xl border border-slate-200 px-5 data-[state=open]:border-teal data-[state=open]:shadow-lg transition"
                  data-testid={`pricing-faq-item-${idx}`}
                >
                  <AccordionTrigger className="text-left text-navy font-semibold text-[15px] hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 text-[15px] leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Free 10-Minute Consultation CTA */}
      <section className="py-16">
        <div className="container-x">
          <div
            className="bg-white rounded-3xl border border-slate-200 shadow-md p-8 md:p-10 text-center max-w-3xl mx-auto"
            data-testid="pricing-consultation-cta"
          >
            <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-teal">
              <Phone size={14} /> Free 10-Minute Consultation
            </span>
            <h3 className="mt-4 text-navy font-bold text-2xl md:text-3xl">
              Free 10-Minute Consultation
            </h3>
            <p className="mt-3 text-slate-600 leading-relaxed max-w-xl mx-auto">
              Talk with our team about your registration pathway, documentation, audit preparation
              or Stage 2 requirements.
            </p>
            <Link
              to="/contact"
              className="mt-6 btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-sm font-semibold"
              data-testid="pricing-consultation-book-cta"
            >
              Book Discovery Call <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
